import React, {
  useEffect,
  useMemo,
  useState,
  memo,
  useCallback,
  useRef,
} from 'react';
import Tabs, { TabPane } from 'rc-tabs';
import SelectNumbersStep from './components/select-numbers-step';
import ContactDetailsStep, {
  FormValues,
} from './components/contact-details-step/contact-details-step';
import PurchasePhoneNumberFooter from './components/purchase-phone-number-footer';
import PurchasePhoneNumberCart from './components/purchase-phone-number-cart/purchase-phone-number-cart';
import ClearAllPhoneNumberCartModal from '../../modals/clear-all-phone-number-cart-modal';
import PhoneNumberBillingSummaryModal from '../../modals/phone-number-billing-summary-modal';
import {
  PhoneNumberPurchaseStep,
  TabConfig,
  PurchasePhoneNumberTabs,
} from './purchase-phone-number-tabs';
import { PurchasePhoneNumberFormProps } from './purchase-phone-number-form-container';
import {
  AddPhoneNumbersToCartPayload,
  RemovePhoneNumberFromCartPayload,
  PurchasePhoneNumbersCartPayload,
} from '../../../../types/phone-numbers';
import { getIsRequestPending } from '../../../../../../shared/utils/get-request-status';
import {
  executeOnRequestStatus,
  redirectWithoutRefresh,
} from '../../../../../../shared/utils';
import usePaddleIntegration from '../../../../../../shared/hooks/script-integrations/usePaddleIntegration';
import usePaddleCustomerAuthToken from '../../../../../../shared/hooks/use-paddle-customer-auth-token';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';

const PurchasePhoneNumberTabForm: React.FC<PurchasePhoneNumberFormProps> = memo(
  ({
    phoneNumbersInCart,
    hasErrorsInContactDetailsForm,
    formattedPhoneNumbersData,
    addPhoneNumbersToCartRequest,
    removePhoneNumberFromCartRequest,
    getPhoneNumbersCartRequest,
    purchasePhoneNumbersCartRequest,
    purchasePhoneNumbersCartRequestState,
    purchasePhoneNumbersCartResponse,
    resetPurchasePhoneNumbersCartRequest,
    removePhoneNumberFromCartRequestState,
    shAccountId,
    userEmail,
    paddleCustomerId,
    paddlePolling,
    startPaddlePollingThunk,
    freeNumbersQuota,
    refreshUserSettings,
    isDemoAccount,
  }) => {
    // add chevron to the tabs if there are more than one tab
    useEffect(() => {
      const bsTabsTab = document.querySelectorAll('.bs-tabs-tab');

      if (bsTabsTab?.length > 0) {
        bsTabsTab.forEach((tab, index) => {
          if (index < bsTabsTab.length - 1) {
            const chevronDiv = document.createElement('div');
            chevronDiv.className = 'purchase-phone-number__tabs--item--chevron';
            chevronDiv.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 12L11 8L6.5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
            tab.after(chevronDiv);
          }
        });
      }
    }, []);

    const { getToken: getCustomerAuthToken } = usePaddleCustomerAuthToken(
      paddleCustomerId,
    );

    const [activeKey, setActiveKey] = useState<PhoneNumberPurchaseStep>(
      PhoneNumberPurchaseStep.SelectNumber,
    );
    const [
      showPurchasePhoneNumberCart,
      setShowPurchasePhoneNumberCart,
    ] = useState(false);
    const [showClearAllCartModal, setShowClearAllCartModal] = useState(false);
    const [showBillingSummaryModal, setShowBillingSummaryModal] = useState(
      false,
    );
    const [
      contactDetailsFormValues,
      setContactDetailsFormValues,
    ] = useState<FormValues | null>(null);
    const [
      showGlobalRestrictionModal,
      setShowGlobalRestrictionModal,
    ] = useState(false);
    const [clearAllCartModalText] = useState({
      title: 'Remove Phone Numbers',
      description:
        'Are you sure you want to remove all phone numbers from your cart?',
      buttons: {
        primary: 'Yes',
        secondary: 'Cancel',
      },
    });

    // Paddle checkout refs
    const checkoutOpenRef = useRef(false);
    const checkoutHandledRef = useRef(false);
    const [isPaddleCheckoutOpen, setIsPaddleCheckoutOpen] = useState(false);

    const isPaddlePolling =
      paddlePolling?.isActive &&
      paddlePolling?.applicationType === 'dialer-phone-number';

    const { isReady: isPaddleReady, openCheckout } = usePaddleIntegration({
      onCheckoutComplete: () => {
        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }
        checkoutHandledRef.current = true;
        checkoutOpenRef.current = false;
        setIsPaddleCheckoutOpen(false);
        resetPurchasePhoneNumbersCartRequest();
        redirectWithoutRefresh('/dialer/phone-numbers');
        startPaddlePollingThunk({
          shAccountId,
          applicationType: 'dialer-phone-number',
          ...(purchasePhoneNumbersCartResponse?.dialerCartId && {
            dialerCartId: purchasePhoneNumbersCartResponse.dialerCartId,
          }),
        });
      },
      onCheckoutError: () => {
        checkoutHandledRef.current = true;
        checkoutOpenRef.current = false;
        setIsPaddleCheckoutOpen(false);
        resetPurchasePhoneNumbersCartRequest();
      },
      onCheckoutClosed: () => {
        if (!checkoutHandledRef.current) {
          checkoutOpenRef.current = false;
          setIsPaddleCheckoutOpen(false);
          resetPurchasePhoneNumbersCartRequest();
        }
      },
    });

    // Handle Paddle checkout opening when purchase request succeeds
    useEffect(() => {
      executeOnRequestStatus({
        status: purchasePhoneNumbersCartRequestState?.status,
        onSuccess: async () => {
          if (checkoutOpenRef.current) return;

          const paddleTransactionId =
            purchasePhoneNumbersCartResponse?.paddleTransactionId;

          // Refresh user settings to update freeNumbersQuota after free numbers are processed
          refreshUserSettings();

          // All free numbers — no Paddle payment needed, treat as purchase completed
          if (!paddleTransactionId) {
            resetPurchasePhoneNumbersCartRequest();
            setShowBillingSummaryModal(false);
            redirectWithoutRefresh('/dialer/phone-numbers');
            return;
          }

          // Mixed cart (free + paid) — free numbers already processed server-side,
          // reload cart to reflect updated state before opening Paddle checkout
          getPhoneNumbersCartRequest();

          if (!isPaddleReady) {
            resetPurchasePhoneNumbersCartRequest();
            setShowBillingSummaryModal(false);
            return;
          }

          checkoutOpenRef.current = true;
          checkoutHandledRef.current = false;
          setIsPaddleCheckoutOpen(true);
          setShowBillingSummaryModal(false);

          const authToken = await getCustomerAuthToken();
          openCheckout({
            transactionId: paddleTransactionId,
            ...(!paddleCustomerId && { customer: { email: userEmail } }),
            ...(authToken && { customerAuthToken: authToken }),
            settings: {
              displayMode: 'overlay',
              variant: 'one-page',
              theme: 'light',
              ...(paddleCustomerId && { allowLogout: false }),
            },
          });
        },
      });
    }, [purchasePhoneNumbersCartRequestState?.status]);

    const handleRemovePhoneNumberFromCart = useCallback(
      (phoneNumber: string) => {
        const payload: RemovePhoneNumberFromCartPayload = {
          phoneNumber: phoneNumber.replace(/\s/g, ''),
        };
        removePhoneNumberFromCartRequest(payload);
      },
      [removePhoneNumberFromCartRequest],
    );

    const handleToggleNumberSelection = (phoneNumber: string) => {
      const normalizedPhoneNumber = phoneNumber.replace(/\s/g, '');
      const isInCart = phoneNumbersInCart.some(
        (item) => item.phoneNumber.replace(/\s/g, '') === normalizedPhoneNumber,
      );
      if (isInCart) {
        handleRemovePhoneNumberFromCart(phoneNumber);
      } else {
        // Find the phone number data from constants
        const numberData = formattedPhoneNumbersData.find(
          (item) =>
            item.phoneNumber.replace(/\s/g, '') === normalizedPhoneNumber,
        );

        if (numberData) {
          const payload: AddPhoneNumbersToCartPayload = {
            phoneNumber: numberData.phoneNumber.replace(/\s/g, ''),
            region: numberData.region,
            locality: numberData.locality,
            countryCode: numberData.countryISOCode,
            pricePerQuarter: numberData.pricePerQuarter.replace('$', ''),
            addressRequirement: numberData.addressRequirement.toLowerCase(),
            numberType: numberData.numberType.toLowerCase(),
          };
          addPhoneNumbersToCartRequest(payload);
        }
      }
    };

    const totalPrice = useMemo(() => {
      const total = phoneNumbersInCart.reduce((sum, item) => {
        const price = parseFloat(item.pricePerQuarter.replace('$', ''));
        return sum + price;
      }, 0);
      return `$${total.toFixed(2)}/quarter`;
    }, [phoneNumbersInCart]);

    const isSelectNumberStepCompleted = phoneNumbersInCart.length !== 0;
    const isContactDetailsStepCompleted = !hasErrorsInContactDetailsForm;
    const isPurchaseRequestPending = getIsRequestPending(
      purchasePhoneNumbersCartRequestState?.status,
    );

    const getIsTabDisabled = (key: string): boolean => {
      if (key === PhoneNumberPurchaseStep.SelectNumber) {
        return false;
      }

      if (key === PhoneNumberPurchaseStep.ContactDetails) {
        return phoneNumbersInCart.length === 0;
      }
      return false;
    };
    const getIsNextButtonDisabled = (): boolean => {
      if (activeKey === PhoneNumberPurchaseStep.SelectNumber) {
        return phoneNumbersInCart.length === 0;
      }
      if (activeKey === PhoneNumberPurchaseStep.ContactDetails) {
        return hasErrorsInContactDetailsForm || phoneNumbersInCart.length === 0;
      }
      return false;
    };

    const tabs: TabConfig[] = useMemo(
      () => [
        {
          key: PhoneNumberPurchaseStep.SelectNumber,
          label: 'Select a Number(s)',
          isCompleted: isSelectNumberStepCompleted,
        },
        {
          key: PhoneNumberPurchaseStep.ContactDetails,
          label: 'Contact Details',
          isCompleted: isContactDetailsStepCompleted,
        },
      ],
      [isSelectNumberStepCompleted, isContactDetailsStepCompleted],
    );

    const handleTabChange = (key: string) => {
      setActiveKey(key as PhoneNumberPurchaseStep);
    };

    const onShowPurchasePhoneNumberCart = () => {
      setShowPurchasePhoneNumberCart(true);
    };

    const onClosePurchasePhoneNumberCart = () => {
      setShowPurchasePhoneNumberCart(false);
    };
    const onCloseClearAllCartModal = () => {
      setShowClearAllCartModal(false);
    };

    const handleNextButtonClick = () => {
      if (activeKey === PhoneNumberPurchaseStep.SelectNumber) {
        setActiveKey(PhoneNumberPurchaseStep.ContactDetails);
        onClosePurchasePhoneNumberCart();
      }
      if (
        activeKey === PhoneNumberPurchaseStep.ContactDetails &&
        !getIsNextButtonDisabled() &&
        contactDetailsFormValues
      ) {
        onClosePurchasePhoneNumberCart();
        setShowBillingSummaryModal(true);
      }
    };

    const handleBillingSummarySubmit = () => {
      if (isDemoAccount) {
        setShowGlobalRestrictionModal(true);
        return;
      }
      if (!contactDetailsFormValues) return;

      const payload: PurchasePhoneNumbersCartPayload = {
        firstName: contactDetailsFormValues.firstName,
        lastName: contactDetailsFormValues.lastName,
        companyName: contactDetailsFormValues.companyName,
        companyWebsite: contactDetailsFormValues.companyWebsite,
        address: contactDetailsFormValues.address,
        country: contactDetailsFormValues.country,
        postal: contactDetailsFormValues.zipCode,
        registeredPhoneNumber: contactDetailsFormValues.phoneNumber,
      };

      if (contactDetailsFormValues.state) {
        payload.state = contactDetailsFormValues.state;
      }

      if (contactDetailsFormValues.city) {
        payload.city = contactDetailsFormValues.city;
      }

      purchasePhoneNumbersCartRequest(payload);
    };

    const handleClearAllClick = () => {
      const payload: RemovePhoneNumberFromCartPayload = {};
      removePhoneNumberFromCartRequest(payload);
    };

    useEffect(() => {
      getPhoneNumbersCartRequest();
    }, [getPhoneNumbersCartRequest]);

    const phoneNumbersInCartForDisplay = useMemo(
      () =>
        phoneNumbersInCart.map((item) => ({
          id: item.id,
          phoneNumber: item.phoneNumber,
          formattedRegion: item.formattedRegion,
          region: item.region,
          locality: item.locality,
          numberType: item.numberType,
          pricePerQuarter: item.pricePerQuarter,
          countryISOCode: item.countryISOCode,
          isFreeNumber: item.isFreeNumber,
        })),
      [phoneNumbersInCart],
    );

    const isLoading =
      isPurchaseRequestPending || isPaddleCheckoutOpen || isPaddlePolling;

    return (
      <div className="purchase-phone-number-container">
        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          prefixCls="bs-tabs"
          className="bs-tabs-small purchase-phone-number__tabs"
        >
          {tabs.map((tab) => (
            <TabPane
              key={tab.key}
              tab={<PurchasePhoneNumberTabs tab={tab} activeKey={activeKey} />}
              disabled={getIsTabDisabled(tab.key)}
            >
              {tab.key === PhoneNumberPurchaseStep.SelectNumber && (
                <SelectNumbersStep
                  formattedPhoneNumbersData={formattedPhoneNumbersData}
                  onToggleNumberSelection={handleToggleNumberSelection}
                  freeNumbersQuota={freeNumbersQuota}
                />
              )}
              {tab.key === PhoneNumberPurchaseStep.ContactDetails && (
                <ContactDetailsStep
                  onFormValuesChange={setContactDetailsFormValues}
                  initialValues={contactDetailsFormValues ?? undefined}
                />
              )}
            </TabPane>
          ))}
        </Tabs>
        <PurchasePhoneNumberFooter
          selectedNumbersCount={phoneNumbersInCart.length}
          totalPrice={totalPrice}
          isNextButtonDisabled={getIsNextButtonDisabled() || isPaddlePolling}
          isNextButtonLoading={isLoading}
          onNextButtonClick={handleNextButtonClick}
          nextButtonText={
            activeKey === PhoneNumberPurchaseStep.ContactDetails
              ? isPaddlePolling
                ? 'Processing Payment...'
                : 'Submit & Purchase'
              : 'Next'
          }
          shouldShow={true}
          onViewCartClick={onShowPurchasePhoneNumberCart}
        />

        <PurchasePhoneNumberCart
          show={showPurchasePhoneNumberCart}
          onClose={onClosePurchasePhoneNumberCart}
          phoneNumbers={phoneNumbersInCartForDisplay}
          isNextButtonDisabled={getIsNextButtonDisabled() || isPaddlePolling}
          isNextButtonLoading={isLoading}
          onNextButtonClick={handleNextButtonClick}
          nextButtonText={
            activeKey === PhoneNumberPurchaseStep.ContactDetails
              ? isPaddlePolling
                ? 'Processing Payment...'
                : 'Submit & Purchase'
              : 'Next'
          }
          onClearAllClick={handleClearAllClick}
          removePhoneNumberFromCart={handleRemovePhoneNumberFromCart}
          totalPrice={totalPrice}
          removePhoneNumberFromCartRequestState={
            removePhoneNumberFromCartRequestState
          }
          setActiveKey={setActiveKey}
        />

        <ClearAllPhoneNumberCartModal
          show={showClearAllCartModal}
          onSubmit={() => {
            handleClearAllClick();
          }}
          clearAllCartModalText={clearAllCartModalText}
          onClose={onCloseClearAllCartModal}
          phoneNumbers={phoneNumbersInCartForDisplay}
        />

        <PhoneNumberBillingSummaryModal
          show={showBillingSummaryModal}
          onClose={() => setShowBillingSummaryModal(false)}
          onSubmit={handleBillingSummarySubmit}
          isLoading={isLoading}
          totalPhoneNumbers={phoneNumbersInCart.length}
          totalAmount={totalPrice}
          phoneNumbersInCart={phoneNumbersInCart}
        />
        <GlobalRestrictionModal
          show={showGlobalRestrictionModal}
          onClose={() => setShowGlobalRestrictionModal(false)}
          title="Purchasing phone numbers is disabled in demo mode"
          content="This is a demo account with sample data. Signup to purchase phone numbers"
          primaryBtnLabel="Sign up"
        />
      </div>
    );
  },
);

PurchasePhoneNumberTabForm.displayName = 'PurchasePhoneNumberTabForm';

export default PurchasePhoneNumberTabForm;
